import React, { useState } from 'react';

interface PreferencesCollectionProps {
  onComplete: (preferences: UserPreferences) => void;
  onSkip: () => void;
}

interface UserPreferences {
  gender: 'male' | 'female' | 'unisex';
  interests: string[];
  show_adult_products: boolean;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'unisex', label: 'Prefer not to specify' },
];

const INTEREST_OPTIONS = [
  { value: 'sports', label: 'Sports & Fitness', icon: '🏃‍♂️' },
  { value: 'nutrition', label: 'Nutrition & Supplements', icon: '💊' },
  { value: 'electronics', label: 'Electronics & Gadgets', icon: '📱' },
  { value: 'fashion', label: 'Fashion & Clothing', icon: '👕' },
  { value: 'beauty', label: 'Beauty & Personal Care', icon: '💄' },
  { value: 'home', label: 'Home & Garden', icon: '🏠' },
  { value: 'books', label: 'Books & Education', icon: '📚' },
  { value: 'gaming', label: 'Gaming & Entertainment', icon: '🎮' },
  { value: 'automotive', label: 'Automotive', icon: '🚗' },
  { value: 'travel', label: 'Travel & Outdoor', icon: '✈️' },
];

const PreferencesCollection: React.FC<PreferencesCollectionProps> = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState<UserPreferences>({
    gender: 'unisex',
    interests: [],
    show_adult_products: false,
  });

  const handleGenderChange = (gender: 'male' | 'female' | 'unisex') => {
    setPreferences(prev => ({ ...prev, gender }));
  };

  const handleInterestToggle = (interest: string) => {
    setPreferences(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleAdultProductsToggle = () => {
    setPreferences(prev => ({ ...prev, show_adult_products: !prev.show_adult_products }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(preferences);
    }
  };

  const handleSkip = () => {
    onSkip();
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return true; // Gender is always valid (has default)
      case 2:
        return preferences.interests.length > 0;
      case 3:
        return true; // Adult products is optional
      default:
        return false;
    }
  };

  const renderStep1 = () => (
    <div className="text-center">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Tell us about yourself</h2>
        <p className="text-gray-300 text-lg">This helps us personalize your mystery shopping experience</p>
      </div>
      
      <div className="space-y-4">
        {GENDER_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleGenderChange(option.value as 'male' | 'female' | 'unisex')}
            className={`w-full p-4 rounded-xl border-2 transition-all duration-200 ${
              preferences.gender === option.value
                ? 'border-purple-400 bg-purple-500/30 text-white shadow-lg shadow-purple-500/25'
                : 'border-purple-500/50 bg-black/20 text-gray-300 hover:border-purple-400 hover:bg-purple-500/10 hover:shadow-lg hover:shadow-purple-500/20'
            }`}
          >
            <span className="text-lg font-medium">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="text-center">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">What interests you?</h2>
        <p className="text-gray-300 text-lg">Select all that apply (at least one required)</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {INTEREST_OPTIONS.map((option) => (
          <button
            key={option.value}
            onClick={() => handleInterestToggle(option.value)}
            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
              preferences.interests.includes(option.value)
                ? 'border-purple-400 bg-purple-500/30 text-white shadow-lg shadow-purple-500/25'
                : 'border-purple-500/50 bg-black/20 text-gray-300 hover:border-purple-400 hover:bg-purple-500/10 hover:shadow-lg hover:shadow-purple-500/20'
            }`}
          >
            <div className="text-2xl mb-2">{option.icon}</div>
            <div className="text-sm font-medium">{option.label}</div>
          </button>
        ))}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="text-center">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-white mb-4">Content preferences</h2>
        <p className="text-gray-300 text-lg">Help us show you the right products</p>
      </div>
      
      <div className="space-y-6">
        <div className="p-6 rounded-xl border-2 border-purple-500/30 bg-black/20 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <div className="text-left">
              <h3 className="text-lg font-medium text-white mb-2">Adult-only products</h3>
              <p className="text-gray-400 text-sm">Include products that may not be suitable for all audiences</p>
            </div>
            <button
              onClick={handleAdultProductsToggle}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                preferences.show_adult_products ? 'bg-purple-500' : 'bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  preferences.show_adult_products ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressBar = () => (
    <div className="w-full bg-gray-700 rounded-full h-2 mb-8">
      <div
        className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${(currentStep / 3) * 100}%` }}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <div className="bg-black/30 backdrop-blur-lg rounded-2xl p-8 border border-purple-500/30 shadow-2xl shadow-purple-500/20">
          {renderProgressBar()}
          
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          
          <div className="flex justify-between mt-8">
            <button
              onClick={handleSkip}
              className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
            >
              Skip for now
            </button>
            
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
                isStepValid()
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-purple-500/25'
                  : 'bg-gray-600 text-gray-400 cursor-not-allowed'
              }`}
            >
              {currentStep === 3 ? 'Complete Setup' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesCollection;
